import React, { createContext, useCallback, useContext, useState } from 'react';
import { usePipelineContext } from './pipelineContext';
import { ColumnControlContextProvider } from '@/context/columnControlContext.jsx';
import { useDebtorFilters } from '@/components/tables/DebtorsTable';

export const isClosedProspectStage = (stage) => stage?.closed || stage?.completed;

const initialState = {
	pipelineId: null,
	pipelineName: '',
	objectType: 'deal',
	isProspect: false,
	isClaims: false,
	isEndorsement: false,
	dealStages: {},
	refreshStatus: {},
	defaultHiddenColumns: [],
	updateRefreshStatus: (stageId, status) => {},
	updateDealStages: (stageId, deals, options) => {},
	clearDealStages: () => {},
	shiftDeal: (existingStage, newStage, existingId, newId, deal) => {},
	debtorFilters: {},
};

const KanbanContext = createContext(initialState);

export const useKanbanContext = () => {
	const context = useContext(KanbanContext);
	if (!context) {
		throw new Error('useKanbanContext must be used within a KanbanContextProvider');
	}
	return context;
};

export const KanbanContextProvider = ({
	pipelineId,
	objectType = 'deal',
	defaultHiddenColumns = [],
	children,
}) => {
	const isProspect = objectType === 'prospect';
	const { stages = [], pipeline } = usePipelineContext(pipelineId);

	const pipelineName = pipeline?.label ?? pipeline?.pipelineName;

	const isClaims = !isProspect && pipelineId == process.env.REACT_APP_PIPELINE_CLAIMS;
	const isEndorsement = !isProspect && pipelineId == process.env.REACT_APP_PIPELINE_ENDORSEMENTS;

	if (isProspect) {
		defaultHiddenColumns = stages.filter(isClosedProspectStage).map((s) => s.id);
	}

	const [dealStages, setDealStages] = useState({});
	const [refreshStatus, setRefreshStatus] = useState({});

	const updateDealStages = useCallback((stageId, deals, { useIncomingOrder = false } = {}) => {
		setDealStages((prev) => {
			const currentDeals = prev[stageId] || [];
			const filteredDeals = deals.filter(
				(d) => d.dealStage === stageId || d.stage === stageId,
			);

			let nextDeals;

			if (useIncomingOrder) {
				nextDeals = filteredDeals;
			} else {
				const incomingMap = new Map(filteredDeals.map((deal) => [deal.id, deal]));

				const preservedOrder = currentDeals
					.filter((deal) => incomingMap.has(deal.id))
					.map((deal) => incomingMap.get(deal.id));

				const existingIds = new Set(currentDeals.map((deal) => deal.id));
				const newDeals = filteredDeals.filter((deal) => !existingIds.has(deal.id));

				nextDeals = [...preservedOrder, ...newDeals];
			}

			const isSame =
				currentDeals.length === nextDeals.length &&
				currentDeals.every((deal, index) => deal === nextDeals[index]);

			if (isSame) {
				return prev;
			}

			return {
				...prev,
				[stageId]: nextDeals,
			};
		});
	}, []);

	const clearDealStages = useCallback(() => {
		setDealStages({});
	}, []);

	const shiftDeal = useCallback((existingStage, newStage, existingId, newId, deal) => {
		if (newStage === existingStage && newId === existingId) return;
		if (newStage === existingStage) {
			setDealStages((prev) => {
				const column = [...(prev[existingStage] || [])];
				column.splice(existingId, 1);
				column.splice(newId ?? column.length, 0, deal);
				return {
					...prev,
					[existingStage]: column,
				};
			});
		} else {
			setDealStages((prev) => {
				const sourceColumn = [...(prev[existingStage] || [])];
				sourceColumn.splice(existingId, 1);

				const alreadyInDestination = (prev[newStage] || []).some((d) => d.id === deal.id);
				let destinationColumn = prev[newStage];
				if (!alreadyInDestination) {
					destinationColumn = [...(prev[newStage] || [])];
					destinationColumn.splice(newId ?? destinationColumn.length, 0, deal);
				}

				return {
					...prev,
					[existingStage]: sourceColumn,
					[newStage]: destinationColumn,
				};
			});
		}
	}, []);

	const updateRefreshStatus = (stageId, status) => {
		setRefreshStatus((prev) => ({ ...prev, [stageId]: status === true }));
	};

	const debtorFilters = useDebtorFilters();

	const contextValue = {
		pipelineId,
		pipelineName,
		objectType,
		isProspect,
		isClaims,
		isEndorsement,
		defaultHiddenColumns,
		dealStages,
		refreshStatus,
		updateRefreshStatus,
		updateDealStages,
		clearDealStages,
		shiftDeal,
		debtorFilters,
	};

	return (
		<KanbanContext.Provider value={contextValue}>
			<ColumnControlContextProvider
				value={pipelineId}
				defaultHiddenColumns={defaultHiddenColumns}
				variant='kanban'
			>
				{children}
			</ColumnControlContextProvider>
		</KanbanContext.Provider>
	);
};

export default KanbanContext;
